import EventBus from 'eventing-bus';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Alert, Button, CircularProgress, Typography, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { CheckCircle as CheckIcon, Email as EmailIcon, Sms as SmsIcon, WhatsApp as WhatsAppIcon, Phone as PhoneIcon, Extension as ExtensionIcon } from '@mui/icons-material';

import {
  getIntegrationStatus,
  createIntegration,
  getIntegrations,
  deleteIntegration,
  getIntegrationTools,
  refreshIntegrationToken,
  testIntegrationConnection
} from '../../store/Integration';

// Helper to generate label from field name (e.g., TWILIO_ACCOUNT_SID -> Account SID)
const getFieldLabel = (fieldName) => {
  return fieldName
    .replace(/^[A-Z]+_/, '') // Remove prefix like TWILIO_, WHATSAPP_
    .replace(/_/g, ' ')       // Replace underscores with spaces
    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words
};

// Helper to determine field type from name
const getFieldType = (fieldName) => {
  const lowerName = fieldName.toLowerCase();
  if (lowerName.includes('token') || lowerName.includes('secret') || lowerName.includes('auth')) {
    return 'password';
  }
  return 'text';
};

// Helper to get icon based on tool name
const getToolIcon = (toolName) => {
  const name = toolName?.toLowerCase() || '';
  if (name.includes('gmail') || name.includes('email')) return <EmailIcon />;
  if (name.includes('twilio') || name.includes('sms')) return <SmsIcon />;
  if (name.includes('whatsapp')) return <WhatsAppIcon />;
  if (name.includes('phone') || name.includes('call') || name.includes('voice')) return <PhoneIcon />;
  return <ExtensionIcon />;
};

const ConnectionStatus = ({ toolName, onConnect, compact = false, showAddOnly = false, showConnectOnly = false }) => {
  const dispatch = useDispatch();

  const [credentials, setCredentials] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);

  // Normalize tool name for consistency
  const normalizedToolName = toolName?.toLowerCase() || '';

  // Redux state
  const { integrationStatus, integrationTest, integrationRefresh, integrations, tools } = useSelector(state => state.Integration);

  // Get status for this specific tool from dynamic state
  const statusState = integrationStatus[normalizedToolName] || {};
  const status = statusState['data'];
  const loading = statusState['loading'];

  // Get test and refresh state for this tool
  const testState = integrationTest[normalizedToolName] || {};
  const refreshState = integrationRefresh[normalizedToolName] || {};

  // Find tool configuration
  const tool = tools.data?.find(t => t.name.toLowerCase() === normalizedToolName);
  const authMethod = tool?.authMethod; // Don't default to oAuth2 - wait for tool to load
  const requiredFields = tool?.requiredFields || [];
  const toolsLoading = tools.loading;

  useEffect(() => {
    if (!toolName) return;

    // Only fetch status if data doesn't exist and not currently loading
    const currentStatusState = integrationStatus[normalizedToolName];
    const hasStatusData = currentStatusState?.data !== undefined || currentStatusState?.error !== undefined;

    if (!hasStatusData && !currentStatusState?.loading) {
      dispatch(getIntegrationStatus(normalizedToolName));
    }

    // Fetch tools only once if not loaded and not loading
    if (!tools.data?.length && !tools.loading) {
      dispatch(getIntegrationTools());
    }
  }, [dispatch, toolName, normalizedToolName, integrationStatus, tools.data?.length, tools.loading]);

  // Success callback for OAuth/API key connection
  const handleConnectionSuccess = () => {
    setConnecting(false);
    setShowConnectDialog(false);
    setCredentials({});
    dispatch(getIntegrations());
    if (onConnect) onConnect();
  };

  // Failure callback for connection
  const handleConnectionFailure = (error) => {
    setConnecting(false);
    EventBus.publish('error', error || `Failed to connect ${toolName}`);
  };

  const handleConnect = async () => {
    if (authMethod === 'apiKey' && requiredFields.length > 0) {
      // For API key auth, validate required fields
      const missingFields = requiredFields.filter(field => {
        const fieldName = typeof field === 'object' ? field.name : field;
        return !credentials[fieldName];
      });
      if (missingFields.length > 0) {
        EventBus.publish('error', `Please fill in all required fields`);
        return;
      }
    }

    setConnecting(true);

    try {
      if (authMethod === 'oAuth2') {
        // OAuth flow - saga handles popup with callbacks
        dispatch(createIntegration({
          name: tool?.name || toolName,
          authType: 'oAuth2',
          onSuccess: handleConnectionSuccess,
          onFailure: handleConnectionFailure
        }));
      } else {
        // API Key flow
        dispatch(createIntegration({
          name: tool?.name || toolName,
          authType: 'apiKey',
          ...credentials,
          onSuccess: handleConnectionSuccess,
          onFailure: handleConnectionFailure
        }));
      }
    } catch (error) {
      console.error(`Failed to connect ${toolName}:`, error);
      EventBus.publish('error', `Failed to connect ${toolName}`);
      setConnecting(false);
    }
  };

  const handleDisconnect = () => {
    const displayName = tool?.name || toolName;
    if (!window.confirm(`Are you sure you want to disconnect your ${displayName} account?`)) {
      return;
    }

    setConnecting(true);

    // Find the integration for this tool
    const integration = integrations?.find(int => int['name']?.toLowerCase() === normalizedToolName && int['isConnected']);

    if (integration) dispatch(deleteIntegration(integration._id));
    else EventBus.publish('error', `${displayName} integration not found`);

    setConnecting(false);
  }

  const handleRefreshToken = () => { dispatch(refreshIntegrationToken(normalizedToolName)) }

  const handleTestConnection = () => {
    // Find the integration for this tool
    const integration = integrations?.find(int => int['name']?.toLowerCase() === normalizedToolName && int['isConnected']);
    if (!integration) return EventBus.publish('error', `${tool?.name || toolName} integration not found`);

    dispatch(testIntegrationConnection(normalizedToolName, {}));
  };

  const openConnectDialog = () => {
    // Prevent connecting if tool config isn't loaded yet
    if (!tool || !authMethod) {
      EventBus.publish('error', `Loading ${toolName} configuration, please try again...`);
      return;
    }
    if (authMethod === 'oAuth2') handleConnect();
    else {
      setCredentials({});
      setShowConnectDialog(true);
    }
  };

  // Render credential fields dynamically
  const renderCredentialFields = () => {
    return requiredFields.map(field => {
      // Handle both string and object formats for requiredFields
      const fieldName = typeof field === 'object' ? field.name : field;
      const label = typeof field === 'object' && field.label ? field.label : getFieldLabel(fieldName);
      const type = typeof field === 'object' && field.type ? field.type : getFieldType(fieldName);

      return (
        <TextField
          fullWidth
          type={type}
          label={label}
          key={fieldName}
          margin="normal"
          value={credentials[fieldName] || ''}
          onChange={(e) => setCredentials({ ...credentials, [fieldName]: e.target.value })}
          helperText={type === 'password' ? 'This field will be stored securely' : undefined}
        />
      );
    });
  };

  // Check if all required fields are filled
  const isFormValid = requiredFields.every(field => {
    const fieldName = typeof field === 'object' ? field['name'] : field;
    return credentials[fieldName];
  });

  const Icon = getToolIcon(toolName);
  const displayName = tool?.['name'] || toolName;

  // Show loading while status OR tools are loading
  if (loading || toolsLoading)
    return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}> <CircularProgress size={20} /> </Box>

  // SHOW ADD ONLY MODE - Just render "Add Another Account" button with connection dialog
  if (showAddOnly) {
    return (
      <>
        <button
          disabled={connecting}
          className="add-account-btn"
          onClick={openConnectDialog}
        >
          {connecting
            ? <><i className='fa fa-spinner fa-spin'></i> Connecting...</>
            : <><i className='fa fa-plus'></i> Add Another Account</>
          }
        </button>

        {/* Connection Dialog for API Key auth */}
        {authMethod === 'apiKey' && requiredFields.length > 0 && (
          <Dialog
            fullWidth
            maxWidth="sm"
            open={showConnectDialog}
            onClose={() => setShowConnectDialog(false)}
          >
            <DialogTitle>Connect {displayName} Account</DialogTitle>
            <DialogContent>
              {/* Help Link */}
              {tool?.['helpLink'] && (
                <Box sx={{ mb: 2, mt: 1 }}>
                  <a
                    target="_blank"
                    href={tool['helpLink']}
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <span style={{ fontSize: '14px' }}>❓</span>
                    Need help? View {displayName} setup guide
                  </a>
                </Box>
              )}
              <Box sx={{ pt: 1 }}> {renderCredentialFields()} </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConnectDialog(false)}>Cancel</Button>
              <button
                onClick={handleConnect}
                className="btn-style-new"
                disabled={connecting || !isFormValid}
              >
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  }

  // SHOW CONNECT ONLY MODE - Just render "Connect" button with connection dialog (for IntegrationBox initial state)
  if (showConnectOnly) {
    return (
      <>
        <button
          disabled={connecting}
          className="btn-style-new"
          onClick={openConnectDialog}
        >
          {connecting
            ? <><i className='fa fa-spinner fa-spin spinner-margin'></i> Connecting...</>
            : <><i className='fa fa-plug spinner-margin'></i> Connect {displayName}</>
          }
        </button>

        {/* Connection Dialog for API Key auth */}
        {authMethod === 'apiKey' && requiredFields.length > 0 && (
          <Dialog
            fullWidth
            maxWidth="sm"
            open={showConnectDialog}
            onClose={() => setShowConnectDialog(false)}
          >
            <DialogTitle>Connect {displayName} Account</DialogTitle>
            <DialogContent>
              {/* Help Link */}
              {tool?.['helpLink'] && (
                <Box sx={{ mb: 2, mt: 1 }}>
                  <a
                    target="_blank"
                    href={tool['helpLink']}
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <span style={{ fontSize: '14px' }}>❓</span>
                    Need help? View {displayName} setup guide
                  </a>
                </Box>
              )}
              <Box sx={{ pt: 1 }}> {renderCredentialFields()} </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConnectDialog(false)}>Cancel</Button>
              <button
                onClick={handleConnect}
                className="btn-style-new"
                disabled={connecting || !isFormValid}
              >
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  }

  // COMPACT VIEW
  if (compact) {
    if (!status || !status['isConnected']) {
      return (
        <>
          <Alert
            sx={{ mb: 2 }}
            severity="warning"
            action={
              <Button
                size="small"
                disabled={connecting}
                onClick={openConnectDialog}
              >
                {connecting ? 'Connecting...' : `Connect ${displayName}`}
              </Button>
            }
          >
            <Box>
              <Typography variant="body2"> {status?.['error'] || `${displayName} account not connected`} </Typography>
              {status?.['accountStatus'] && status['accountStatus'] !== 'active' &&
                <Typography variant="caption" color="error" display="block"> Account Status: {status['accountStatus']} </Typography>
              }
            </Box>
          </Alert>

          {/* Connection Dialog for API Key auth */}
          {authMethod === 'apiKey' && requiredFields.length > 0 && (
            <Dialog
              fullWidth
              maxWidth="sm"
              open={showConnectDialog}
              onClose={() => setShowConnectDialog(false)}
            >
              <DialogTitle>Connect {displayName} Account</DialogTitle>
              <DialogContent>
                {/* Help Link */}
                {tool?.['helpLink'] && (
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <a
                      target="_blank"
                      href={tool['helpLink']}
                      rel="noopener noreferrer"
                      style={{ color: '#1976d2', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <span style={{ fontSize: '14px' }}>❓</span>
                      Need help? View {displayName} setup guide
                    </a>
                  </Box>
                )}
                <Box sx={{ pt: 1 }}> {renderCredentialFields()} </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowConnectDialog(false)}>Cancel</Button>
                <button
                  onClick={handleConnect}
                  className="btn-style-new"
                  disabled={connecting || !isFormValid}
                >
                  {connecting ? 'Connecting...' : 'Connect'}
                </button>
              </DialogActions>
            </Dialog>
          )}
        </>
      );
    }

    // Connected - compact view
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color="success"
            icon={<CheckIcon />}
            label={`Connected: ${status.email || status.phoneNumber || displayName}`}
          />
          {status.isTokenValid === false && <Chip size="small" color="warning" label="Token expired" />}
          <Button
            size="small"
            color="error"
            variant="text"
            disabled={connecting}
            onClick={handleDisconnect}
            sx={{ ml: 'auto', fontSize: '0.75rem' }}
          >
            Disconnect
          </Button>
        </Box>
        {(status['balance'] !== undefined || (status['accountStatus'] && status['accountStatus'] !== 'active')) && (
          <Box sx={{ mt: 1 }}>
            {status.balance !== undefined && <Typography variant="caption" color="text.secondary" display="block"> Balance: ${status['balance']} </Typography>}
            {status['accountStatus'] && status['accountStatus'] !== 'active' && <Typography variant="caption" color="error" display="block"> Status: {status['accountStatus']} </Typography>}
          </Box>
        )}
      </Box>
    );
  }

  // FULL VIEW
  if (!status || !status['isConnected']) {
    return (
      <>
        <Alert
          icon={Icon}
          severity="warning"
          action={
            <Button
              size="small"
              sx={{ ml: 2 }}
              variant="contained"
              disabled={connecting}
              onClick={openConnectDialog}
            >
              {connecting ? 'Connecting...' : `Connect ${displayName} Account`}
            </Button>
          }
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}> {displayName} not connected </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}> {status?.error || `Connect your ${displayName} account to use this feature`} </Typography>
          {status?.['accountStatus'] && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}> Account Status: {status['accountStatus']} </Typography>}
        </Alert>

        {/* Connection Dialog for API Key auth */}
        {authMethod === 'apiKey' && requiredFields.length > 0 && (
          <Dialog
            fullWidth
            maxWidth="sm"
            open={showConnectDialog}
            onClose={() => setShowConnectDialog(false)}
          >
            <DialogTitle>Connect {displayName} Account</DialogTitle>
            <DialogContent> <Box sx={{ pt: 1 }}> {renderCredentialFields()} </Box> </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowConnectDialog(false)}>Cancel</Button>
              <button
                onClick={handleConnect}
                className="btn-style-new"
                disabled={connecting || !isFormValid}
              >
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  }

  // Connected - full view
  return (
    <>
      <Alert
        icon={Icon}
        severity="success"
        sx={{ '.MuiAlert-message': { width: '100%' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}> {displayName} Connected </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}> {status.email || status.phoneNumber || 'Connected'} </Typography>
            {status['accountSid'] &&
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}> Account SID: {status['accountSid']} </Typography>
            }
            {status.balance !== undefined &&
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}> Balance: ${status['balance']} </Typography>
            }
            {status['accountName'] &&
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}> Account: {status['accountName']} </Typography>
            }
            {status['expiryDate'] &&
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}> Token expires: {new Date(status['expiryDate']).toLocaleDateString()} </Typography>
            }
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Show refresh button for OAuth tokens that are expired */}
            {authMethod === 'oAuth2' && status['isTokenValid'] === false && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleRefreshToken}
                disabled={refreshState['loading']}
              >
                {refreshState['loading'] ? 'Refreshing...' : 'Refresh Token'}
              </Button>
            )}
            {/* Show test button for API key auth */}
            {authMethod === 'apiKey' && (
              <Button
                size="small"
                variant="outlined"
                disabled={testState['loading']}
                onClick={handleTestConnection}
              >
                {testState['loading'] ? 'Testing...' : 'Test Connection'}
              </Button>
            )}
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={connecting}
              onClick={handleDisconnect}
            >
              {connecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </Box>
        </Box>
      </Alert>
    </>
  );
};

export default ConnectionStatus;
